/**
 * useManifestFormState — shared state hook for manifest-driven connection
 * edit dialogs (managed connections + service-provider connections).
 *
 * Owns the mutable form state (displayName, parameterValues,
 * selectedParameterSetName), the original snapshot, and the derived dirty
 * flags. The dialog that mounts this hook still owns *ingress* (how to seed
 * from its data source) and *egress* (how to persist on Save) — this hook
 * is agnostic to both.
 *
 * Usage:
 *   const form = useManifestFormState();
 *   useEffect(() => { form.seed({ displayName, values, parameterSetName }); }, [conn]);
 *   ...
 *   <Input value={form.displayName} onChange={(_,d) => form.setDisplayName(d.value)} />
 *   ...
 *   if (form.hasChanges) { await persist(form.displayName, form.formValues, form.selectedParameterSetName); }
 */

import { useCallback, useMemo, useState } from 'react';
import type { FormValues } from '@/types/connectionParameters';

export interface ManifestFormSeed {
  displayName: string;
  formValues: FormValues;
  selectedParameterSetName: string | undefined;
}

export interface UseManifestFormStateResult {
  /** Current display name (edit-controlled). */
  displayName: string;
  setDisplayName: (value: string) => void;

  /** Current parameter values (edit-controlled). */
  formValues: FormValues;
  setFormValues: (values: FormValues) => void;

  /** Currently selected parameter-set name (auth-type dropdown). */
  selectedParameterSetName: string | undefined;
  /**
   * Change the selected parameter set. Also clears the form values, because
   * switching auth types invalidates the previous field set — matches the
   * managed-connection edit behavior in `EditConnectionDialog`.
   */
  setSelectedParameterSetName: (name: string) => void;

  /** True when displayName differs from the seeded original. */
  hasDisplayNameChanges: boolean;
  /** True when any parameter value differs from the seeded original. */
  hasFormChanges: boolean;
  /** True when the selected parameter set differs from the seeded original. */
  hasAuthTypeChanges: boolean;
  /** Any of the above. */
  hasChanges: boolean;

  /**
   * (Re-)seed state from the given values. Also captures them as the
   * new "original" snapshot for dirty tracking. Idempotent — safe to call
   * from a `useEffect` on prop changes.
   */
  seed: (seed: ManifestFormSeed) => void;

  /**
   * Partial seed: refreshes only `formValues` + `selectedParameterSetName`
   * without touching `displayName`. Used by the managed dialog, where
   * `displayName` is available synchronously from the `connection` prop
   * but the parameter values arrive later from the `useConnection` API
   * call. Prevents in-progress `displayName` edits from being wiped when
   * the details load.
   */
  seedFormValues: (formValues: FormValues, selectedParameterSetName: string | undefined) => void;

  /** Restore state to the last seeded snapshot without re-fetching. */
  resetToOriginal: () => void;
}

/**
 * Deep-equality check via JSON.stringify. Adequate here because
 * `FormValues` leaves are primitives / small plain objects produced by the
 * manifest form itself.
 */
const isEqual = (a: unknown, b: unknown): boolean =>
  JSON.stringify(a) === JSON.stringify(b);

export function useManifestFormState(): UseManifestFormStateResult {
  const [displayName, setDisplayName] = useState('');
  const [formValues, setFormValues] = useState<FormValues>({});
  const [selectedParameterSetName, setSelectedParameterSetNameState] =
    useState<string | undefined>(undefined);

  const [originalDisplayName, setOriginalDisplayName] = useState('');
  const [originalFormValues, setOriginalFormValues] = useState<FormValues>({});
  const [originalParameterSetName, setOriginalParameterSetName] =
    useState<string | undefined>(undefined);

  const seed = useCallback((next: ManifestFormSeed) => {
    setDisplayName(next.displayName);
    setFormValues(next.formValues);
    setSelectedParameterSetNameState(next.selectedParameterSetName);
    setOriginalDisplayName(next.displayName);
    setOriginalFormValues(next.formValues);
    setOriginalParameterSetName(next.selectedParameterSetName);
  }, []);

  const seedFormValues = useCallback(
    (nextValues: FormValues, nextParameterSetName: string | undefined) => {
      setFormValues(nextValues);
      setSelectedParameterSetNameState(nextParameterSetName);
      setOriginalFormValues(nextValues);
      setOriginalParameterSetName(nextParameterSetName);
    },
    [],
  );

  const resetToOriginal = useCallback(() => {
    setDisplayName(originalDisplayName);
    setFormValues(originalFormValues);
    setSelectedParameterSetNameState(originalParameterSetName);
  }, [originalDisplayName, originalFormValues, originalParameterSetName]);

  const setSelectedParameterSetName = useCallback((name: string) => {
    setSelectedParameterSetNameState(name);
    // Switching auth types invalidates the previous field set — the user
    // has to re-enter values for the new provider.
    setFormValues({});
  }, []);

  const hasDisplayNameChanges = displayName.trim() !== originalDisplayName.trim();
  const hasFormChanges = useMemo(
    () => !isEqual(formValues, originalFormValues),
    [formValues, originalFormValues],
  );
  const hasAuthTypeChanges =
    selectedParameterSetName !== undefined &&
    selectedParameterSetName !== originalParameterSetName;

  return {
    displayName,
    setDisplayName,
    formValues,
    setFormValues,
    selectedParameterSetName,
    setSelectedParameterSetName,
    hasDisplayNameChanges,
    hasFormChanges,
    hasAuthTypeChanges,
    hasChanges: hasDisplayNameChanges || hasFormChanges || hasAuthTypeChanges,
    seed,
    seedFormValues,
    resetToOriginal,
  };
}
